import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListPricelistsResult } from '@whinerp/client';
import { Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * The price list this customer is on (G-102).
 *
 * `account.pricelist_id` was read by the pricing engine and written by nothing
 * at all, so a negotiated price could be built, tested and never reach the
 * customer it was negotiated with. This is the writer.
 *
 * "Standard pricing" is a real option in the list, not the absence of one. A
 * customer whose negotiated terms have ended goes back to standard, and a
 * control with no way to say that leaves somebody assigning them to a list
 * called "Standard" - which is a second answer to what standard means.
 */
export function AccountPricelistPanel({
  accountId,
  current,
}: {
  accountId: string;
  current: string | null;
}): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();

  const lists = useQuery<ListPricelistsResult, ApiError>({
    queryKey: ['pricelists'],
    queryFn: () => api.listPricelists({}),
  });

  const assign = useMutation<unknown, ApiError, string | null>({
    mutationFn: (pricelistId) =>
      api.setAccountPricelist({ path: { accountId }, body: { pricelistId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account', accountId] }),
  });

  return (
    <Panel data-region="account-pricelist">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('account.pricelist')}</SectionLabel>

        {assign.error ? (
          <p data-pricelist-refusal className="text-dense text-danger-text">
            {assign.error.message}
          </p>
        ) : null}

        {lists.isPending ? (
          <Skeleton className="h-input w-full" />
        ) : lists.isError ? (
          // Quiet: the record is readable without this, and an error banner
          // here would sit on top of the work about a field beside it.
          null
        ) : (
          <label className="flex flex-col gap-1">
            <span className="sr-only">{t('account.pricelist')}</span>
            <select
              data-account-pricelist
              value={current ?? ''}
              disabled={assign.isPending}
              onChange={(event) => assign.mutate(event.target.value === '' ? null : event.target.value)}
              className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
            >
              <option value="">{t('account.pricelist.standard')}</option>
              {lists.data.pricelists
                .filter((list) => list.active)
                .map((list) => (
                  <option key={list.pricelistId} value={list.pricelistId}>
                    {list.name}
                  </option>
                ))}
            </select>
          </label>
        )}

        <p className="text-meta text-ink-meta">{t('account.pricelist.help')}</p>
      </div>
    </Panel>
  );
}
